import { useEffect, useState } from 'react';
import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { Select } from 'antd';
import { FilterPanel } from '@/components/common/FilterPanel';
import { getFilterValue, type SetFilters } from '@/utils/filters';

type ReviewStatus = 'pending' | 'accepted' | 'rejected';

const STATUS_FIELDS = ['accepted_at', 'rejected_at'] as const;
type StatusField = (typeof STATUS_FIELDS)[number];

// Accepting clears `rejected_at` and rejecting clears `accepted_at`, so each
// status maps to plain AND-ed null checks on the two timestamps.
const STATUS_FILTERS: Record<
  ReviewStatus,
  Partial<Record<StatusField, 'null' | 'nnull'>>
> = {
  pending: { accepted_at: 'null', rejected_at: 'null' },
  accepted: { accepted_at: 'nnull' },
  rejected: { rejected_at: 'nnull' },
};

const isSet = (
  filters: CrudFilter[],
  field: StatusField,
  operator: 'null' | 'nnull'
) => getFilterValue<boolean>(filters, field, operator) === true;

const statusFromFilters = (filters: CrudFilter[]): ReviewStatus | undefined =>
  (Object.keys(STATUS_FILTERS) as ReviewStatus[]).find((status) =>
    STATUS_FIELDS.every((field) => {
      const operator = STATUS_FILTERS[status][field];
      return operator
        ? isSet(filters, field, operator)
        : !isSet(filters, field, 'null') && !isSet(filters, field, 'nnull');
    })
  );

interface ReviewStatusFilterProps {
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
}

/** Pending / accepted / rejected, applied as null checks on both timestamps. */
export const ReviewStatusFilter = ({
  filters,
  confirm,
  clearFilters,
  setFilters,
}: ReviewStatusFilterProps) => {
  const t = useTranslate();
  const current = statusFromFilters(filters);
  const [pending, setPending] = useState<ReviewStatus | undefined>(current);

  useEffect(() => setPending(current), [current]);

  // Refine keys filters by field *and* operator, so every combination is
  // written each time to clear the ones the new status doesn't use.
  const apply = (status: ReviewStatus | undefined) =>
    setFilters(
      STATUS_FIELDS.flatMap((field) =>
        (['null', 'nnull'] as const).map((operator) => ({
          field,
          operator,
          value:
            status && STATUS_FILTERS[status][field] === operator
              ? true
              : undefined,
        }))
      ),
      'merge'
    );

  return (
    <FilterPanel
      onFilter={() => {
        apply(pending);
        confirm();
      }}
      onReset={() => {
        setPending(undefined);
        apply(undefined);
        clearFilters?.();
      }}
    >
      <Select
        allowClear
        value={pending}
        onChange={(value?: ReviewStatus) => setPending(value)}
        style={{ minWidth: 160 }}
        options={(Object.keys(STATUS_FILTERS) as ReviewStatus[]).map(
          (status) => ({
            value: status,
            label: t(`feedback.reviews.status.${status}`),
          })
        )}
      />
    </FilterPanel>
  );
};
